import React, { useState, useRef, useEffect, useCallback } from 'react';

import { FaBrain, FaTimes, FaPaperPlane, FaLifeRing, FaChevronDown, FaChevronUp, FaQuestionCircle } from 'react-icons/fa';

import CustomChatBotIcon from './CustomChatBotIcon';
import './IMSChatBot.css';

import { useWorkspace } from '../contexts/WorkspaceContext';


// ── Suggested quick prompts ───────────────────────────────────────────────────
const SUGGESTIONS = [
  "What's critically low?",
  "List items by category",
  "Which items have no category?",
  "Storage usage",
];

// ── Component ─────────────────────────────────────────────────────────────────
const IMSChatBot = () => {
  const { imsFetch } = useWorkspace();
  const { activeWorkspace } = useWorkspace();
  const [, setCatalogContext] = useState("");

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'support'
  const [openFaqIndex, setOpenFaqIndex] = useState(null); // Accordion state
  const [messages, setMessages] = useState([
    {
      text: "👋 Welcome! I'm your **IMS AI Assistant**. How can I help you manage your inventory today?",
      isBot: true,
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const isTypingRef = useRef(false);
  const messagesEndRef = useRef(null);

  const FAQS = [
    {
      q: "BVS Scanner Connection Issues",
      a: "BVS scanners require a valid registration in the platform. Go to the Device Manager (/device-manager) and click 'Pair Scanner' using the scanner's Device ID. If the scanner cannot connect, generate the Wi-Fi config QR code from the manager page, scan it with the scanner to pair the device, and check the scanner connection logs."
    },
    {
      q: "Adding Team Members & Roles",
      a: "Workspace Admins or Owners can invite members by going to the Team & Access page (/ims-users) and clicking 'Invite Member'. Provide their email and assign them a workspace role (Admin, Manager, or User). Click 'Generate Invite Link' and copy the link for the invitee to sign up."
    },
    {
      q: "Access Denied Error Resolutions",
      a: "Standard registered user accounts are restricted from accessing settings, catalogs, or team configurations. If you hit an Access Denied wall, ask your workspace Administrator to promote your role to Admin or Manager from the Team & Access page (/ims-users)."
    },
    {
      q: "IMS Settings & Plan Storage Limit Upgrades",
      a: "Storage plan limits can be upgraded from the IMS Settings tab (/ims-settings). Note: Only the Workspace Owner or Admins can modify settings parameters or initiate billing upgrades."
    },
    {
      q: "Generating & Printing Barcode Sheets",
      a: "You can configure and bulk-generate labels using the Barcode Generator page (/generator). Select template parameters, custom margins, and quantity counts, then click 'Download PDF' to print."
    },
    {
      q: "Other Issues",
      a: "If you have any other issues, questions, or custom troubleshooting inquiries not listed above, you can chat with the AI assistant directly."
    }
  ];

  // Fetches latest catalog data
  const getLatestCatalogContext = useCallback(async () => {
    try {
      let wsInfo = '';
      if (activeWorkspace) {
        wsInfo = `ACTIVE WORKSPACE:\nWorkspace Name: ${activeWorkspace.name || 'N/A'}\nWorkspace ID: ${activeWorkspace.id || 'N/A'}\nCurrent User Role: ${activeWorkspace.currentUserRole || 'N/A'}\n\n`;
      }

      const mastersRes = await imsFetch('/api/ims/masters');
      const mastersData = await mastersRes.json();
      
      if (mastersData.success && mastersData.masters.length > 0) {
        let masterContext = mastersData.masters.map(m => `Master ID: ${m.id}, Name: ${m.name}, Category: ${m.category || 'N/A'}`).join('\n');
        
        let allItems = [];
        for (const master of mastersData.masters) {
          const itemsRes = await imsFetch(`/api/ims/masters/${master.id}/items`);
          const itemsData = await itemsRes.json();
          if (itemsData.success) {
            allItems = [...allItems, ...itemsData.items];
          }
        }
        
        let totalStock = 0;
        const itemContext = allItems.length > 0
          ? allItems.map(item => {
              totalStock += parseInt(item.stock) || 0;
              return `Barcode: ${item.barcode}, Name: ${item.name}, Stock: ${item.stock}, Category: ${item.category || 'N/A'}`;
            }).join('\n')
          : "No physical items in stock.";
        
        const maxCapacity = 15000;
        const usagePercent = ((totalStock / maxCapacity) * 100).toFixed(1);
        return `${wsInfo}WAREHOUSE STATUS:\nTotal Storage Capacity: ${maxCapacity} units\nCurrent Usage: ${totalStock} units (${usagePercent}% full)\n\nMASTER CATALOG (Product Types):\n${masterContext}\n\nPHYSICAL ITEMS (Stock):\n${itemContext}`;
      }
      return `${wsInfo}The inventory is currently empty. There are no items.`;
    } catch (err) {
      console.error("Failed to load catalog context for AI:", err);
      return "There was an error fetching the inventory data. Please try again later.";
    }
  }, [imsFetch, activeWorkspace]);

  useEffect(() => {
    if (!isOpen || !activeWorkspace?.id) return;
    // Initial fetch to populate context when chatbot opens
    getLatestCatalogContext().then(ctx => setCatalogContext(ctx));
  }, [isOpen, messages.length, activeWorkspace, getLatestCatalogContext]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const sendMessage = async (text) => {
    if (isTypingRef.current) return; // Synchronous lock to prevent React batching double-clicks
    isTypingRef.current = true;
    
    const userText = text || input.trim();
    if (!userText) {
      isTypingRef.current = false;
      return;
    }
    
    setInput('');
    setMessages(prev => [...prev, { text: userText, isBot: false }]);
    setIsTyping(true);

    try {
      // 🚨 Ensure we always fetch the absolutely latest catalog data before answering!
      const freshestContext = await getLatestCatalogContext();
      setCatalogContext(freshestContext);

      const AI_API_URL = process.env.REACT_APP_AI_SERVER_URL || 'http://localhost:8000';
      
      const response = await fetch(`${AI_API_URL}/api/ims-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          message: userText,
          catalog_context: freshestContext
        }),
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const data = await response.json();
      setMessages(prev => [...prev, { text: data.reply, isBot: true }]);
    } catch (error) {
      console.error("AI Chat error:", error);
      setMessages(prev => [...prev, { 
        text: "I am having trouble connecting to the AI server. Is the Python backend running?", 
        isBot: true 
      }]);
    } finally {
      setIsTyping(false);
      isTypingRef.current = false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  // Render markdown-style bold (**text**)
  const renderText = (text) =>
    text.split(/\*\*(.*?)\*\*/g).map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );

  const renderMessage = (text) =>
    text.split('\n').map((line, i) => (
      <span key={i}>
        {renderText(line)}
        {i < text.split('\n').length - 1 && <br />}
      </span>
    ));

  return (
    <div className={`ims-chatbot-container ${isOpen ? 'open' : ''}`}>
      {/* Floating Button */}
      {!isOpen && (
        <button className="ims-chatbot-fab" onClick={() => setIsOpen(true)} title="Ask IMS AI">
          <CustomChatBotIcon className="fab-icon" />
          <span className="fab-pulse"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="ims-chatbot-window">
          {/* Header */}
          <div className="ims-chatbot-header">
            <div className="chatbot-header-left">
              <div className="chatbot-avatar"><CustomChatBotIcon /></div>
              <div>
                <div className="chatbot-title">IMS AI Assistant</div>
                <div className="chatbot-status"><span className="status-dot"></span> Online · Inventory Mode</div>
              </div>
            </div>
            <button className="chatbot-close" onClick={() => setIsOpen(false)}><FaTimes /></button>
          </div>

          {/* Navigation Tabs */}
          <div className="ims-chatbot-tabs">
            <button 
              type="button"
              className={`chatbot-tab-btn ${activeTab === 'chat' ? 'active' : ''}`} 
              onClick={() => setActiveTab('chat')}
            >
              <CustomChatBotIcon className="tab-icon-small" /> AI Chat
            </button>
            <button 
              type="button"
              className={`chatbot-tab-btn ${activeTab === 'support' ? 'active' : ''}`} 
              onClick={() => setActiveTab('support')}
            >
              <FaLifeRing className="tab-icon-small" /> Troubleshoot
            </button>
          </div>

          {activeTab === 'support' ? (
            <div className="ims-chatbot-support">
              <div className="support-intro">
                <FaQuestionCircle className="support-intro-icon" />
                <div>
                  <h4>Common Platform Issues</h4>
                  <p>Choose an item to view troubleshooting steps or ask the AI to elaborate.</p>
                </div>
              </div>
              <div className="faq-accordion">
                {FAQS.map((faq, idx) => {
                  const isFaqOpen = openFaqIndex === idx;
                  return (
                    <div key={idx} className={`faq-card ${isFaqOpen ? 'expanded' : ''}`}>
                      <button 
                        type="button"
                        className="faq-trigger" 
                        onClick={() => setOpenFaqIndex(isFaqOpen ? null : idx)}
                      >
                        <span className="faq-q-text">{faq.q}</span>
                        {isFaqOpen ? <FaChevronUp className="faq-chevron" /> : <FaChevronDown className="faq-chevron" />}
                      </button>
                      {isFaqOpen && (
                        <div className="faq-content">
                          <p className="faq-a-text">{faq.a}</p>
                          <button 
                            type="button"
                            className="faq-action-btn"
                            onClick={() => {
                              setActiveTab('chat');
                              if (faq.q === "Other Issues") {
                                sendMessage("I need help with a custom platform issue.");
                              } else {
                                sendMessage(`Help me troubleshoot: ${faq.q}`);
                              }
                            }}
                          >
                            <FaBrain style={{ marginRight: '6px' }} /> {faq.q === "Other Issues" ? "Chat with AI" : "Ask AI for details"}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="ims-chatbot-messages">
                {messages.map((msg, i) => (
                  <div key={i} className={`chat-bubble ${msg.isBot ? 'bot' : 'user'}`}>
                    {msg.isBot && <div className="bubble-avatar"><CustomChatBotIcon /></div>}
                    <div className="bubble-text">{renderMessage(msg.text)}</div>
                  </div>
                ))}
                {isTyping && (
                  <div className="chat-bubble bot">
                    <div className="bubble-avatar"><CustomChatBotIcon /></div>
                    <div className="bubble-text typing-dots">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Suggestions */}
              <div 
                className="ims-chatbot-suggestions"
                onMouseDown={(e) => {
                  const slider = e.currentTarget;
                  slider.isDown = true;
                  slider.startX = e.pageX - slider.offsetLeft;
                  slider.scrollLeftStart = slider.scrollLeft;
                  slider.style.cursor = 'grabbing';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.isDown = false;
                  e.currentTarget.style.cursor = 'grab';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.isDown = false;
                  e.currentTarget.style.cursor = 'grab';
                }}
                onMouseMove={(e) => {
                  const slider = e.currentTarget;
                  if (!slider.isDown) return;
                  e.preventDefault();
                  const x = e.pageX - slider.offsetLeft;
                  const walk = (x - slider.startX) * 2;
                  slider.scrollLeft = slider.scrollLeftStart - walk;
                }}
                style={{ cursor: 'grab' }}
              >
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} type="button" className="suggestion-chip" onClick={() => sendMessage(s)}>
                    {s}
                  </button>
                ))}
              </div>

              {/* Input */}
              <form className="ims-chatbot-input" onSubmit={handleSubmit}>
                <input
                  type="text"
                  placeholder="Ask about stock, alerts, forecasts..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  disabled={isTyping}
                  autoFocus
                />
                <button type="submit" disabled={isTyping || !input.trim()}>
                  <FaPaperPlane />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default IMSChatBot;
